import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Stack, Snackbar, Alert,
  Switch, FormControlLabel, Grid
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Security as SecurityIcon, CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { roleAPI } from '../../services/api';
import AssignPermissionsDialog from './AssignPermissionsDialog';
import { useAccessControl, RESOURCES, ACTIONS } from '../../hooks/useAccessControl';

const Roles = () => {
  const { can } = useAccessControl();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openPermissionsDialog, setOpenPermissionsDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  const canReadRoles = can(RESOURCES.ROLES, ACTIONS.READ);
  const canCreateRoles = can(RESOURCES.ROLES, ACTIONS.CREATE);
  const canUpdateRoles = can(RESOURCES.ROLES, ACTIONS.UPDATE);
  const canDeleteRoles = can(RESOURCES.ROLES, ACTIONS.DELETE);
  const canAssignPermissions = can(RESOURCES.PERMISSIONS, ACTIONS.ASSIGN);

  useEffect(() => {
    if (!canReadRoles) {
      setRoles([]);
      return;
    }

    fetchRoles();
  }, [canReadRoles]);

  const fetchRoles = async () => {
    if (!canReadRoles) {
      return;
    }

    try {
      setLoading(true);
      const response = await roleAPI.getAll();
      setRoles(response.data.data || response.data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load roles', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (role = null) => {
    if (role && !canUpdateRoles) {
      setSnackbar({ open: true, message: 'You do not have permission to update roles', severity: 'error' });
      return;
    }

    if (!role && !canCreateRoles) {
      setSnackbar({ open: true, message: 'You do not have permission to create roles', severity: 'error' });
      return;
    }

    if (role) {
      setFormData({
        name: role.name,
        description: role.description || '',
        is_active: role.is_active
      });
      setSelectedRole(role);
    } else {
      setFormData({ name: '', description: '', is_active: true });
      setSelectedRole(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRole(null);
    setFormData({ name: '', description: '', is_active: true });
  };

  const handleSubmit = async () => {
    if (selectedRole && !canUpdateRoles) {
      setSnackbar({ open: true, message: 'You do not have permission to update roles', severity: 'error' });
      return;
    }

    if (!selectedRole && !canCreateRoles) {
      setSnackbar({ open: true, message: 'You do not have permission to create roles', severity: 'error' });
      return;
    }

    try {
      setLoading(true);
      if (selectedRole) {
        await roleAPI.update(selectedRole.id, formData);
        setSnackbar({ open: true, message: 'Role updated successfully', severity: 'success' });
      } else {
        await roleAPI.create(formData);
        setSnackbar({ open: true, message: 'Role created successfully', severity: 'success' });
      }
      handleCloseDialog();
      fetchRoles();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Operation failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (role) => {
    if (!canDeleteRoles) {
      setSnackbar({ open: true, message: 'You do not have permission to delete roles', severity: 'error' });
      return;
    }

    setRoleToDelete(role);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setRoleToDelete(null);
  };

  const handleDelete = async () => {
    if (!canDeleteRoles) {
      setSnackbar({ open: true, message: 'You do not have permission to delete roles', severity: 'error' });
      return;
    }

    try {
      await roleAPI.delete(roleToDelete.id);
      setSnackbar({ open: true, message: 'Role deleted successfully', severity: 'success' });
      handleCloseDeleteDialog();
      fetchRoles();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to delete role', severity: 'error' });
    }
  };

  const handleToggleStatus = async (role) => {
    if (!canUpdateRoles) {
      setSnackbar({ open: true, message: 'You do not have permission to update roles', severity: 'error' });
      return;
    }

    try {
      await roleAPI.toggleStatus(role.id);
      setSnackbar({ open: true, message: 'Role status updated', severity: 'success' });
      fetchRoles();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const handleOpenPermissions = (role) => {
    if (!canAssignPermissions) {
      setSnackbar({ open: true, message: 'You do not have permission to assign permissions', severity: 'error' });
      return;
    }

    setSelectedRole(role);
    setOpenPermissionsDialog(true);
  };

  if (!canReadRoles) {
    return (
      <Alert severity="error">
        You do not have permission to view role management.
      </Alert>
    );
  }

  return (
    <Box>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Role Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage user roles and permissions
          </Typography>
        </Box>
        {canCreateRoles && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Role
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Users</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {role.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{role.description || '-'}</TableCell>
                    <TableCell>
                      <Chip label={role.users_count || 0} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Chip label={role.permissions_count || 0} size="small" color="secondary" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={role.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                        label={role.is_active ? 'Active' : 'Inactive'}
                        color={role.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {canAssignPermissions && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenPermissions(role)}
                            title="Manage Permissions"
                          >
                            <SecurityIcon />
                          </IconButton>
                        )}
                        {canUpdateRoles && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenDialog(role)}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {canDeleteRoles && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(role)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                        {canUpdateRoles && (
                          <Switch
                            checked={role.is_active}
                            onChange={() => handleToggleStatus(role)}
                            size="small"
                          />
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || (selectedRole ? !canUpdateRoles : !canCreateRoles)}
          >
            {selectedRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role <strong>{roleToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={!canDeleteRoles}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Permissions Dialog */}
      {openPermissionsDialog && (
        <AssignPermissionsDialog
          open={openPermissionsDialog}
          role={selectedRole}
          onClose={() => {
            setOpenPermissionsDialog(false);
            setSelectedRole(null);
          }}
          onSuccess={() => {
            setSnackbar({ open: true, message: 'Permissions updated successfully', severity: 'success' });
            fetchRoles();
          }}
        />
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Roles;
